import { useEffect, useState } from 'react';

import type { NetworkGraphReadiness } from '../../network/network-graph-artifact';
import type { NetworkRouteClient } from '../../network/network-route-client';

type ReadinessState =
  | { readonly client: NetworkRouteClient; readonly error: string; readonly status: 'error' }
  | {
      readonly client: NetworkRouteClient;
      readonly readiness: NetworkGraphReadiness;
      readonly status: 'ready';
      readonly storagePersisted: boolean | null;
    };

/** Visible, truthful status for the eagerly prepared full offline graph. */
export function NetworkGraphReadinessPanel({ client }: { readonly client: NetworkRouteClient }) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<ReadinessState | null>(null);
  const current = state?.client === client ? state : null;

  useEffect(() => {
    let active = true;
    void client
      .prepare()
      .then(async (readiness) => {
        const storagePersisted =
          (await navigator.storage?.persisted?.().catch(() => false)) ?? null;
        if (active) setState({ client, readiness, status: 'ready', storagePersisted });
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setState({
          client,
          error:
            reason instanceof Error
              ? `Síťový graf není připravený: ${reason.message}`
              : 'Síťový graf se nepodařilo připravit.',
          status: 'error',
        });
      });
    return () => {
      active = false;
    };
  }, [attempt, client]);

  return (
    <aside className="catalog-readiness network-graph-readiness" aria-label="Offline síťový graf">
      <strong>Offline síťový graf CZ + DE</strong>
      {current?.status === 'error' ? (
        <>
          <p role="alert">
            {current.error} Ruční, historické a šablonové trasy zůstávají dostupné.
          </p>
          <button
            type="button"
            className="secondary-action touch-target"
            onClick={() => {
              setState(null);
              setAttempt((value) => value + 1);
            }}
          >
            Připravit graf znovu
          </button>
        </>
      ) : current?.status === 'ready' ? (
        <>
          <p role="status">
            Připraveno offline: {current.readiness.edgeCount.toLocaleString('cs-CZ')} skutečných
            železničních hran; {current.readiness.anchoredDocumentCount.toLocaleString('cs-CZ')} z{' '}
            {current.readiness.catalogDocumentCount.toLocaleString('cs-CZ')} míst katalogu je
            napojeno. Stažený obsah má{' '}
            {compressedSizeLabel(current.readiness.artifactCompressedBytes)}.
          </p>
          <p className="field-description">
            {current.storagePersisted === true
              ? 'Prohlížeč potvrdil ochranu offline úložiště před automatickým uvolněním.'
              : 'Graf je nyní v místní cache; při nedostatku místa jej prohlížeč může později uvolnit.'}
          </p>
        </>
      ) : (
        <p role="status">
          Ověřuji checksumy a připravuji celý síťový graf mimo hlavní editor… Ruční trasa zůstává
          dostupná.
        </p>
      )}
    </aside>
  );
}

function compressedSizeLabel(bytes: number): string {
  const mebibytes = (bytes / (1024 * 1024)).toLocaleString('cs-CZ', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  return `${mebibytes} MiB (${bytes.toLocaleString('cs-CZ')} B)`;
}
